import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import MainCard from '../../../components/ui-component/cards/MainCard';
import AnimateButton from '../../../components/ui-component/extended/AnimateButton';

import { Grid, CircularProgress, Typography, Button, Box, OutlinedInput, InputLabel, FormControl, Modal } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DefaultTextModal } from '../../../components/ui-component/Modal';
import { getDeviceDetail } from '../../../../api/get/Device';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { updateDeviceInfo } from '../../../../api/put/Device';
import { deleteDevice } from '../../../../api/delete/Device';
import useScriptRef from '../../../../hooks/useScriptRef';
import { DeviceStatus, RESPONSE_CODE, responseResult } from '../../../../metadata/constant';

// ==============================|| DEVICE DETAIL ||============================== //

const DeviceEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const scriptedRef = useScriptRef();

    const [device, setDevice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openModal, setOpenModal] = useState(0);

    useEffect(() => {
        const fetchDeviceDetail = async () => {
            try {
                const response = await getDeviceDetail(id);
                if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                    setDevice(response.data);
                } else {
                    setDevice({});
                    setError('기기 상세 조회 중 오류가 발생하였습니다.');
                }
            } catch (err) {
                setError(err.message || 'An error occurred while fetching device detail');
            } finally {
                setLoading(false);
            }
        };

        fetchDeviceDetail();
    }, [id]);

    const handleModalClose = () => {
        setOpenModal(0);
    };

    const handleBackToList = () => {
        navigate(-1);
    };

    const handleDelete = () => {
        setOpenModal(1);
    };

    const handleDeleteDevice = () => {
        const request = async () => {
            try {
                const response = await deleteDevice(id);
                if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                    navigate(`/management/license`);
                } else {
                    setOpenModal(2);
                }
            } catch (e) {
                setOpenModal(2);
            }
        };

        request();
    };

    return (
        <>
            <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <MainCard title="기기 정보 수정">
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                                <CircularProgress />
                            </div>
                        ) : error ? (
                            <Typography color="error" variant="body1" align="center" sx={{ padding: '2rem' }}>
                                {error}
                            </Typography>
                        ) : device ? (
                            <Formik
                                initialValues={{
                                    deviceName: device.device_name,
                                    memo: device.memo,
                                    status: device.status,
                                    ipAddress: device.ip_address,
                                    macAddress: device.MAC,
                                    createdAt: device.created_at,
                                    lastUsed: device.last_used
                                }}
                                validationSchema={Yup.object().shape({
                                    name: Yup.string().min(5).max(50).required('기기명을 입력해 주세요'),
                                    memo: Yup.string().min(0).max(500).optional()
                                })}
                                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                                    try {
                                        if (scriptedRef.current) {
                                            const body = {
                                                device_id: id,
                                                device_name: values.deviceName,
                                                memo: values.memo
                                            };
                                            const response = await updateDeviceInfo(body);
                                            if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                                                setStatus({ success: true });
                                                setSubmitting(false);
                                                navigate(`/management/license/device/${id}`);
                                            } else {
                                                setStatus({ success: false });
                                                setErrors({ submit: response.data?.message || '수정 중 오류가 발생했습니다.' });
                                                setSubmitting(false);
                                                setOpenModal(3);
                                            }
                                        }
                                    } catch (err) {
                                        if (scriptedRef.current) {
                                            setStatus({ success: false });
                                            setErrors({
                                                submit: err.response?.data?.message || err.message || '수정 중 오류가 발생했습니다.'
                                            });
                                            setSubmitting(false);
                                            setOpenModal(3);
                                        }
                                    }
                                }}
                            >
                                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                                    <form noValidate onSubmit={handleSubmit}>
                                        <Box pt={2} pb={2}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <InputLabel required htmlFor="outlined-adornment-device-name">
                                                            기기명
                                                        </InputLabel>
                                                        <OutlinedInput
                                                            id="outlined-adornment-device-name"
                                                            type="text"
                                                            value={values.deviceName ? values.deviceName : ''}
                                                            name="deviceName"
                                                            label="기기명"
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <InputLabel htmlFor="outlined-adornment-memo">메모</InputLabel>
                                                        <OutlinedInput
                                                            id="outlined-adornment-memo"
                                                            type="text"
                                                            value={values.memo ? values.memo : ''}
                                                            name="memo"
                                                            label="메모"
                                                            onBlur={handleBlur}
                                                            onChange={handleChange}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <InputLabel htmlFor="outlined-adornment-ip">IP</InputLabel>
                                                        <OutlinedInput
                                                            id="outlined-adornment-ip"
                                                            type="text"
                                                            value={device.ip_address ? device.ip_address : '-'}
                                                            name="ip"
                                                            label="IP"
                                                            disabled
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <InputLabel htmlFor="outlined-adornment-mac">MAC</InputLabel>
                                                        <OutlinedInput
                                                            id="outlined-adornment-mac"
                                                            type="text"
                                                            value={device.MAC ? device.MAC : '-'}
                                                            name="mac"
                                                            label="MAC"
                                                            disabled
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <InputLabel htmlFor="outlined-adornment-created-at">등록일시</InputLabel>
                                                        <OutlinedInput
                                                            id="outlined-adornment-created-at"
                                                            type="text"
                                                            value={device.created_at ? device.created_at : '-'}
                                                            name="created_at"
                                                            label="등록일시"
                                                            disabled
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <InputLabel htmlFor="outlined-adornment-last-used-at">마지막 사용일시</InputLabel>
                                                        <OutlinedInput
                                                            id="outlined-adornment-last-used-at"
                                                            type="text"
                                                            value={device.last_used ? device.last_used : '-'}
                                                            name="last_used_at"
                                                            label="마지막 사용일시"
                                                            disabled
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <InputLabel htmlFor="outlined-adornment-status">기기 등록 상태</InputLabel>
                                                        <OutlinedInput
                                                            id="outlined-adornment-status"
                                                            type="text"
                                                            value={device.status ? DeviceStatus[device.status] : '-'}
                                                            name="status"
                                                            label="기기 등록 상태"
                                                            disabled
                                                        />
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={theme.typography.buttonBox}>
                                                        <AnimateButton>
                                                            <Button
                                                                disableElevation
                                                                size="large"
                                                                color="secondary"
                                                                onClick={handleBackToList}
                                                            >
                                                                취소하고 돌아가기
                                                            </Button>
                                                        </AnimateButton>
                                                        <AnimateButton>
                                                            <Button
                                                                disableElevation
                                                                size="large"
                                                                variant="contained"
                                                                color="error"
                                                                onClick={handleDelete}
                                                            >
                                                                기기 삭제
                                                            </Button>
                                                        </AnimateButton>
                                                        <AnimateButton>
                                                            <Button
                                                                disableElevation
                                                                size="large"
                                                                variant="contained"
                                                                color="primary"
                                                                type="submit"
                                                            >
                                                                저장
                                                            </Button>
                                                        </AnimateButton>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </form>
                                )}
                            </Formik>
                        ) : (
                            <Typography variant="body1" align="center" sx={{ padding: '2rem' }}>
                                기기 정보를 찾을 수 없습니다.
                            </Typography>
                        )}
                    </MainCard>
                </Grid>
            </Grid>
            <Modal open={openModal > 0} onClose={handleModalClose}>
                <Box sx={theme.typography.modal}>
                    {openModal === 1 ? (
                        <>
                            <Typography id="modal-modal-title" variant="h3" component="h2">
                                등록된 기기 삭제
                            </Typography>
                            <Typography id="modal-modal-description" sx={{ mt: 2, lineHeight: 1.8 }} component="div">
                                등록된 기기를 삭제하실 경우 해당 기기에서 더 이상 브레인 가드 닥터를 이용하실 수 없습니다.
                                <br />
                                다시 이용을 하기 위해서는 기기를 다시 등록 후 활성화 과정을 거쳐야 이용이 가능합니다.
                                <br />
                                그래도 등록된 기기를 삭제 하시겠습니까?
                            </Typography>
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Grid container spacing={2} justifyContent="end">
                                    <Grid item>
                                        <AnimateButton>
                                            <Button
                                                disableElevation
                                                size="large"
                                                type="button"
                                                color="secondary"
                                                onClick={handleModalClose}
                                            >
                                                취소하고 돌아가기
                                            </Button>
                                        </AnimateButton>
                                    </Grid>
                                    <Grid item>
                                        <AnimateButton>
                                            <Button
                                                disableElevation
                                                size="large"
                                                type="submit"
                                                variant="contained"
                                                color="error"
                                                onClick={handleDeleteDevice}
                                            >
                                                등록된 기기 삭제
                                            </Button>
                                        </AnimateButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </>
                    ) : openModal === 2 ? (
                        <>
                            <DefaultTextModal
                                title="기기 삭제 실패"
                                content={`기기 삭제에 실패하였습니다.\n잠시 후 다시 시도해 주세요.\n오류가 지속되는 경우 고객센터로 문의 부탁드립니다.`}
                                open={openModal === 2}
                                onClose={handleModalClose}
                            />
                        </>
                    ) : openModal === 3 ? (
                        <>
                            <DefaultTextModal
                                title="기기 정보 변경 실패"
                                content={`기기 정보 변경에 실패하였습니다.\n잠시 후 다시 시도해 주세요.\n오류가 지속되는 경우 고객센터로 문의 부탁드립니다.`}
                                open={openModal === 3}
                                onClose={handleModalClose}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default DeviceEdit;
